'use client'

import { useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { AiBrain01Icon, CopyIcon, Cursor02Icon, Link01Icon, RocketIcon } from '@hugeicons-pro/core-solid-rounded'
import { Button } from '@/components/ui/button'
import type { InstallFramework } from '@/lib/install-prompt/types'

interface InstallPromptModalProps {
  brandId: string
  prompt: string
  frameworkDetected: InstallFramework | string
  copyLink?: string
  compact?: boolean
}

async function trackCopy(brandId: string) {
  try {
    await fetch(`/api/install-prompts/${brandId}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ event_name: 'install_prompt_copied' }),
    })
  } catch {
    // Copy telemetry is best-effort.
  }
}

export function InstallPromptModal({ brandId, prompt, frameworkDetected, copyLink, compact }: InstallPromptModalProps) {
  const [copied, setCopied] = useState(false)

  async function copyPrompt() {
    await navigator.clipboard.writeText(prompt)
    setCopied(true)
    await trackCopy(brandId)
    window.setTimeout(() => setCopied(false), 1800)
  }

  return (
    <div className={compact ? 'space-y-3' : 'mx-auto w-full max-w-5xl space-y-5'}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-atrium-black/50">Atrium install prompt</p>
          <h1 className={compact ? 'text-base font-semibold text-atrium-black' : 'text-3xl font-semibold text-atrium-black'}>
            Coding-agent-ready install task
          </h1>
          <p className="mt-1 text-sm text-atrium-black/60">Detected framework: {String(frameworkDetected).replace(/_/g, ' ')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="white" size="sm" asChild>
            <a href={`cursor://anysphere.cursor-deeplink/prompt?text=${encodeURIComponent(prompt)}`}>
              <HugeiconsIcon icon={Cursor02Icon} className="size-4" />
              Cursor
            </a>
          </Button>
          <Button variant="white" size="sm" asChild>
            <a href={`claude://new?prompt=${encodeURIComponent(prompt)}`}>
              <HugeiconsIcon icon={AiBrain01Icon} className="size-4" />
              Claude Code
            </a>
          </Button>
          {copyLink && (
            <Button variant="white" size="sm" asChild>
              <a href={copyLink} target="_blank" rel="noreferrer">
                <HugeiconsIcon icon={Link01Icon} className="size-4" />
                Open link
              </a>
            </Button>
          )}
          <Button variant="atrium" size="sm" onClick={copyPrompt}>
            <HugeiconsIcon icon={copied ? RocketIcon : CopyIcon} className="size-4" />
            {copied ? 'Copied' : 'Copy prompt'}
          </Button>
        </div>
      </div>

      <pre className="max-h-[70vh] overflow-auto rounded-lg border border-atrium-border bg-[#111111] p-4 text-xs leading-6 text-white shadow-sm">
        <code>{prompt}</code>
      </pre>
    </div>
  )
}
